import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/theme-toggle";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useCompanyLogo } from "@/hooks/use-company-logo";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarProvider,
  SidebarTrigger,
  SidebarFooter,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import AdminNotificationBell from "@/components/admin-notification-bell";
import { useAdminPushSubscription } from "@/hooks/use-admin-push-subscription";
import NotificationPermissionBanner from "@/components/notification-permission-banner";
import {
  LayoutDashboard,
  Package,
  Users,
  Settings,
  Scale,
  ChefHat,
  PackageCheck,
  LogOut,
  ClipboardList,
  UserCog,
  Undo2,
  Wheat,
  BookOpen,
  CakeSlice,
  FileText,
  ChevronRight,
  Link2,
  ScrollText,
  FileCode2,
  MessageSquare,
  Bell,
  Tags,
  BarChart2,
  Mail,
  Shield,
  ShieldOff,
  Home,
  Truck,
  ShoppingBag,
} from "lucide-react";
export interface AdminSession {
  id: string;
  username: string;
  displayName: string;
  isSuperAdmin: boolean;
  permissions: string[];
}

// Page-level permission map: route path → permission key
const PAGE_PERM_MAP: Record<string, string> = {
  "/admin": "admin.overview",
  "/admin/dashboard": "admin.overview",
  "/admin/statistics": "admin.statistics",
  "/admin/orders": "admin.orders",
  "/admin/products": "admin.products",
  "/admin/price-lists": "admin.pricelists",
  "/admin/clients": "admin.clients",
  "/admin/sellers": "admin.clients",
  "/admin/returns": "admin.returns",
  "/admin/invoicing": "admin.invoicing",
  "/admin/communication": "comm.communication",
  "/admin/support": "comm.support",
  "/admin/notifications": "comm.notifications",
  "/admin/raw-materials": "recipe.rawmaterials",
  "/admin/base-recipes": "recipe.baserecipes",
  "/admin/article-recipes": "recipe.articlerecipes",
  "/production/tragare": "prod.tragare",
  "/production/bagare": "prod.bagare",
  "/production/pakare": "prod.pakare",
  "/admin/settings": "settings.settings",
  "/admin/users": "settings.users",
  "/admin/connections": "settings.connections",
  "/admin/api-docs": "settings.api_docs",
  "/admin/activity-log": "settings.log",
  "/admin/roles": "settings.roles",
};

function canAccess(session: AdminSession | null | undefined, path: string): boolean {
  if (!session) return false;
  if (session.isSuperAdmin) return true;
  const required = PAGE_PERM_MAP[path];
  if (!required) return true; // no permission required
  return session.permissions.includes(required);
}

interface MenuItemDef {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  superAdminOnly?: boolean;
}

const adminMenuItems: MenuItemDef[] = [
  { title: "Översikt", href: "/admin", icon: LayoutDashboard },
  { title: "Statistik", href: "/admin/statistics", icon: BarChart2 },
  { title: "Ordrar", href: "/admin/orders", icon: ClipboardList },
  { title: "Leveransavvikelser", href: "/admin/delivery-adjustments", icon: Truck },
  { title: "Produkter", href: "/admin/products", icon: Package },
  { title: "Prislistor", href: "/admin/price-lists", icon: Tags },
  { title: "Kunder", href: "/admin/clients", icon: Users },
  { title: "Säljare", href: "/admin/sellers", icon: ShoppingBag },
  { title: "Returer", href: "/admin/returns", icon: Undo2 },
  { title: "Fakturering", href: "/admin/invoicing", icon: FileText },
  { title: "Kommunikation", href: "/admin/communication", icon: Mail },
];

const communicationMenuItems: MenuItemDef[] = [
  { title: "Support", href: "/admin/support", icon: MessageSquare },
  { title: "Notifikationer", href: "/admin/notifications", icon: Bell },
];

const settingsSubItems: MenuItemDef[] = [
  { title: "Inställningar", href: "/admin/settings", icon: Settings },
  { title: "Användare", href: "/admin/users", icon: UserCog },
  { title: "Kopplingar", href: "/admin/connections", icon: Link2 },
  { title: "API-docs", href: "/admin/api-docs", icon: FileCode2 },
  { title: "Logg", href: "/admin/activity-log", icon: ScrollText },
  { title: "Roller", href: "/admin/roles", icon: Shield, superAdminOnly: true },
];

const recipeMenuItems: MenuItemDef[] = [
  { title: "Råvarulista", href: "/admin/raw-materials", icon: Wheat },
  { title: "Basrecept", href: "/admin/base-recipes", icon: BookOpen },
  { title: "Artikelrecept", href: "/admin/article-recipes", icon: CakeSlice },
];

const productionMenuItems: MenuItemDef[] = [
  { title: "Trågare", href: "/production/tragare", icon: Scale },
  { title: "Bagare", href: "/production/bagare", icon: ChefHat },
  { title: "Pakare", href: "/production/pakare", icon: PackageCheck },
];

// Standalone access-denied view rendered in place of the restricted page
export function AccessDeniedView() {
  const [, setLocation] = useLocation();
  return (
    <div
      className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center px-4"
      data-testid="access-denied-view"
    >
      <div className="rounded-full bg-destructive/10 p-5">
        <ShieldOff className="h-12 w-12 text-destructive" />
      </div>
      <div className="space-y-2 max-w-sm">
        <h2 className="text-2xl font-semibold tracking-tight">Åtkomst nekad</h2>
        <p className="text-muted-foreground">
          Du har inte behörighet att se den här sidan. Kontakta en super-admin om du behöver tillgång.
        </p>
      </div>
      <Button
        onClick={() => setLocation("/admin")}
        className="gap-2"
        data-testid="button-go-to-dashboard"
      >
        <Home className="h-4 w-4" />
        Gå till översikten
      </Button>
    </div>
  );
}

// Route guard component — shows access-denied view instead of redirecting
export function AdminRouteGuard({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { data: adminSession } = useQuery<AdminSession | null>({
    queryKey: ["/api/admin-auth/me"],
  });

  // Synchronous denial check — no useEffect needed, no transient flash
  const isDenied = !!adminSession
    && !adminSession.isSuperAdmin
    && (() => {
      const required = PAGE_PERM_MAP[location];
      return !!required && !adminSession.permissions.includes(required);
    })();

  if (isDenied) return <AccessDeniedView />;
  return <>{children}</>;
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const logoUrl = useCompanyLogo();
  useAdminPushSubscription();

  const { data: adminSession } = useQuery<AdminSession | null>({
    queryKey: ["/api/admin-auth/me"],
  });

  const { data: adjustmentCount } = useQuery<{ count: number }>({
    queryKey: ["/api/admin/delivery-adjustments/count"],
    refetchInterval: 60_000,
  });

  const handleLogout = async () => {
    await apiRequest("POST", "/api/admin-auth/logout", {});
    queryClient.invalidateQueries({ queryKey: ["/api/admin-auth/me"] });
    setLocation("/");
  };

  const sidebarStyle = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  const visibleAdminMenuItems = adminMenuItems.filter(item => canAccess(adminSession, item.href));
  const visibleCommunicationMenuItems = communicationMenuItems.filter(item => canAccess(adminSession, item.href));
  const visibleSettingsSubItems = settingsSubItems.filter(item => {
    if (item.superAdminOnly) return adminSession?.isSuperAdmin;
    return canAccess(adminSession, item.href);
  });
  const visibleRecipeMenuItems = recipeMenuItems.filter(item => canAccess(adminSession, item.href));
  const visibleProductionMenuItems = productionMenuItems.filter(item => canAccess(adminSession, item.href));

  const settingsActive = settingsSubItems.some(i => location === i.href);

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <Sidebar>
          <SidebarHeader className="p-4">
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="Rätt Order" className="h-8" />
              <Badge variant="secondary" className="text-xs">Admin</Badge>
            </div>
          </SidebarHeader>
          <SidebarContent>
            {visibleAdminMenuItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel>Administration</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleAdminMenuItems.map((item) => (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton
                          asChild
                          isActive={location === item.href}
                        >
                          <Link href={item.href}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            {item.href === "/admin/delivery-adjustments" && (adjustmentCount?.count ?? 0) > 0 && (
                              <Badge className="ml-auto h-5 min-w-[20px] px-1 text-xs bg-destructive text-destructive-foreground" data-testid="badge-adjustment-count">
                                {adjustmentCount!.count}
                              </Badge>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                    {visibleSettingsSubItems.length > 0 && (
                      <Collapsible defaultOpen={settingsActive} className="group/collapsible">
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton data-testid="button-settings-menu">
                              <Settings className="h-4 w-4" />
                              <span>Inställningar</span>
                              <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {visibleSettingsSubItems.map((item) => (
                                <SidebarMenuSubItem key={item.href}>
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={location === item.href}
                                    data-testid={`link-settings-${item.href.split('/').pop()}`}
                                  >
                                    <Link href={item.href}>
                                      <item.icon className="h-4 w-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {visibleCommunicationMenuItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel>Kommunikation</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleCommunicationMenuItems.map((item) => (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton
                          asChild
                          isActive={location === item.href}
                        >
                          <Link href={item.href}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {visibleRecipeMenuItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel>Recept</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleRecipeMenuItems.map((item) => (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton
                          asChild
                          isActive={location === item.href}
                        >
                          <Link href={item.href}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {visibleProductionMenuItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel>Produktion</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleProductionMenuItems.map((item) => (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton
                          asChild
                          isActive={location === item.href}
                        >
                          <Link href={item.href}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </SidebarContent>
          <SidebarFooter className="p-4">
            {adminSession && (
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    {adminSession.displayName?.[0] || adminSession.username?.[0] || "A"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {adminSession.displayName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {adminSession.isSuperAdmin ? "Super-admin" : adminSession.username}
                  </p>
                </div>
              </div>
            )}
          </SidebarFooter>
        </Sidebar>

        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between gap-4 p-3 border-b bg-background">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <div className="flex items-center gap-2">
              <AdminNotificationBell />
              <ThemeToggle />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="button-logout"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </header>
          <main className="flex-1 overflow-auto p-3 sm:p-6">
            <NotificationPermissionBanner subscribeEndpoint="/api/admin/push/subscribe" />
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
